import { useEffect } from 'react';
import { CheckCircle, Download, ArrowRight, User } from 'lucide-react';
 
function downloadReceipt({ paymentId, plan, amount, userName, addOns }) {
  const date = new Date().toLocaleString('en-IN', { dateStyle: 'long', timeStyle: 'short' });
  const subtotal = Math.round(amount / 1.18);
  const gst = amount - subtotal;
  const addonCost = ((addOns?.extraAiAgents || 0) + (addOns?.extraHumanAgents || 0)) * 900 + (addOns?.extraCredits?.price || 0);
  const basePrice = subtotal - addonCost;
  const isTopup = plan?.category === 'topup';

  let breakdownHtml = '';
  let planDisplayName = plan?.name || '';
  if (isTopup) {
    breakdownHtml = `
      <div class="row"><span class="label">Top-up Pack</span><span class="value">₹${subtotal.toLocaleString('en-IN')}</span></div>
    `;
  } else {
    planDisplayName = (basePrice === 0 && addonCost > 0) ? (
      [
        addOns?.extraAiAgents > 0 && `${addOns.extraAiAgents} x Extra AI Agent${addOns.extraAiAgents > 1 ? 's' : ''}`,
        addOns?.extraHumanAgents > 0 && `${addOns.extraHumanAgents} x Extra Human Agent${addOns.extraHumanAgents > 1 ? 's' : ''}`,
        addOns?.extraCredits && `Credit Pack (${addOns.extraCredits.label || addOns.extraCredits.k})`
      ].filter(Boolean).join(" + ")
    ) : plan?.name;

    breakdownHtml = basePrice > 0 ? `
      <div class="row"><span class="label">Base Plan</span><span class="value">₹${basePrice.toLocaleString('en-IN')}</span></div>
    ` : '';
    if (addOns?.extraAiAgents > 0) {
      breakdownHtml += `
      <div class="row" style="padding-left: 12px; border-left: 2px solid #82bbe0; margin-left: 4px;"><span class="label">+ Extra AI Agents (${addOns.extraAiAgents})</span><span class="value">₹${(addOns.extraAiAgents * 900).toLocaleString('en-IN')}</span></div>`;
    }
    if (addOns?.extraHumanAgents > 0) {
      breakdownHtml += `
      <div class="row" style="padding-left: 12px; border-left: 2px solid #82bbe0; margin-left: 4px;"><span class="label">+ Extra Human Agents (${addOns.extraHumanAgents})</span><span class="value">₹${(addOns.extraHumanAgents * 900).toLocaleString('en-IN')}</span></div>`;
    }
    if (addOns?.extraCredits) {
      breakdownHtml += `
      <div class="row" style="padding-left: 12px; border-left: 2px solid #82bbe0; margin-left: 4px;"><span class="label">+ Credit Pack (${addOns.extraCredits.label || addOns.extraCredits.k})</span><span class="value">₹${addOns.extraCredits.price.toLocaleString('en-IN')}</span></div>`;
    }
  }

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8"/>
  <title>EasyChat Receipt – ${paymentId}</title>
  <style>
    body { font-family: system-ui, sans-serif; background: #f9fafb; margin: 0; padding: 40px; color: #111; }
    .card { background: #fff; max-width: 480px; margin: 0 auto; border-radius: 16px; padding: 40px; box-shadow: 0 4px 24px rgba(0,0,0,0.08); }
    .logo { font-size: 22px; font-weight: 900; color: #111; margin-bottom: 4px; }
    .logo span { color: #82bbe0; }
    .badge { display: inline-block; background: #e8f5ed; color: #16a34a; font-size: 11px; font-weight: 700; padding: 3px 10px; border-radius: 20px; margin-bottom: 24px; }
    h2 { font-size: 20px; margin: 0 0 6px; }
    .sub { color: #6b7280; font-size: 13px; margin-bottom: 28px; }
    .divider { border: none; border-top: 1px solid #f0f0f0; margin: 20px 0; }
    .row { display: flex; justify-content: space-between; font-size: 13px; margin-bottom: 10px; }
    .row .label { color: #6b7280; }
    .row .value { font-weight: 600; }
    .row .mono { font-family: monospace; font-size: 11px; color: #82bbe0; }
    .total { font-size: 15px; font-weight: 800; }
    .footer { margin-top: 28px; font-size: 11px; color: #9ca3af; text-align: center; }
  </style>
</head>
<body>
  <div class="card">
    <div class="logo">Easy<span>Chat</span></div>
    <p style="font-size:11px;color:#9ca3af;margin:0 0 16px">Your Conversations, Simplified</p>
    <span class="badge">✓ Payment Confirmed</span>
    <h2>Payment Receipt</h2>
    <p class="sub">Thank you for your purchase. Your plan is now active.</p>
    <hr class="divider"/>
    <div class="row"><span class="label">Customer</span><span class="value">${userName}</span></div>
    <div class="row"><span class="label">Date</span><span class="value">${date}</span></div>
    <div class="row"><span class="label">Payment ID</span><span class="mono">${paymentId}</span></div>
    <div class="row"><span class="label">Plan / Pack</span><span class="value">${planDisplayName}</span></div>
    <hr class="divider"/>
    ${breakdownHtml}
    <div class="row"><span class="label">GST (18%)</span><span class="value">₹${gst.toLocaleString('en-IN')}</span></div>
    <hr class="divider"/>
    <div class="row total"><span>Total Paid</span><span>₹${amount?.toLocaleString('en-IN')}</span></div>
    <div class="row"><span class="label">Status</span><span style="color:#16a34a;font-weight:700">Confirmed</span></div>
    <hr class="divider"/>
    <div class="footer">
      Secured by Razorpay · 256-bit SSL encryption<br/>
      © ${new Date().getFullYear()} EasyChat. All rights reserved.
    </div>
  </div>
</body>
</html>`;
 
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `easychat-receipt-${paymentId}.html`;
  a.click();
  URL.revokeObjectURL(url);
}
 
export default function PaymentSuccess({ paymentId, plan, amount, userName, addOns, onDone }) {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    const mainEl = document.querySelector('main');
    if (mainEl) {
      mainEl.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, []);

  const subtotal = Math.round(amount / 1.18);
  const gst = amount - subtotal;
  const addonCost = ((addOns?.extraAiAgents || 0) + (addOns?.extraHumanAgents || 0)) * 900 + (addOns?.extraCredits?.price || 0);
  const basePrice = subtotal - addonCost;
  const isTopup = plan?.category === 'topup';

  return (
    <div className="w-full bg-gray-50 flex justify-center px-4 py-6 md:py-10">
      <div className="bg-white rounded-3xl shadow-2xl p-10 w-full max-w-lg mx-auto text-center">
        <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle size={44} className="text-green-500" strokeWidth={1.5} />
        </div>
 
        <h2 className="text-2xl font-extrabold text-gray-900 mb-2">Payment Successful!</h2>
        <p className="text-gray-500 text-sm mb-6">
          Your payment has been processed successfully. Your plan is now active.
        </p>
 
        {/* Purchased Plan Banner */}
        <div className="bg-[#82bbe0]/10 border border-[#82bbe0]/30 rounded-2xl p-4 mb-5 text-left">
          <div className="flex items-center gap-2 mb-2">
            <User size={14} className="text-[#82bbe0]" />
            <span className="text-xs font-semibold text-[#82bbe0] uppercase tracking-wide">
              {basePrice === 0 && addonCost > 0 ? "Purchased Add-on" : "Purchased Plan"}
            </span>
          </div>
          <p className="text-base font-bold text-gray-900">
            {basePrice === 0 && addonCost > 0 ? (
              [
                addOns?.extraAiAgents > 0 && `${addOns.extraAiAgents} x Extra AI Agent${addOns.extraAiAgents > 1 ? 's' : ''}`,
                addOns?.extraHumanAgents > 0 && `${addOns.extraHumanAgents} x Extra Human Agent${addOns.extraHumanAgents > 1 ? 's' : ''}`,
                addOns?.extraCredits && `Credit Pack (${addOns.extraCredits.label || addOns.extraCredits.k})`
              ].filter(Boolean).join(" + ")
            ) : plan?.name}
          </p>
          <p className="text-sm text-gray-500 mt-0.5">Activated for <span className="font-semibold text-gray-700">{userName}</span></p>
        </div>
 
        <div className="bg-gray-50 rounded-2xl p-5 mb-6 text-left space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Customer</span>
            <span className="font-semibold text-gray-900">{userName}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Payment ID</span>
            <span className="font-mono text-xs text-[#82bbe0] font-semibold">{paymentId}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Status</span>
            <span className="text-green-600 font-semibold">Confirmed</span>
          </div>

          <div className="border-t border-gray-200/50 my-2 pt-2"></div>

          {isTopup ? (
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Top-up Pack</span>
              <span className="font-semibold text-gray-900">₹{subtotal.toLocaleString('en-IN')}</span>
            </div>
          ) : (
            <>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Base Plan</span>
                <span className="font-semibold text-gray-900">₹{basePrice.toLocaleString('en-IN')}</span>
              </div>
              {addOns?.extraAiAgents > 0 && (
                <div className="flex justify-between text-sm pl-3 border-l-2 border-[#82bbe0]/40">
                  <span className="text-gray-500">+ Extra AI Agents ({addOns.extraAiAgents})</span>
                  <span className="font-semibold text-gray-900">₹{(addOns.extraAiAgents * 900).toLocaleString('en-IN')}</span>
                </div>
              )}
              {addOns?.extraHumanAgents > 0 && (
                <div className="flex justify-between text-sm pl-3 border-l-2 border-[#82bbe0]/40">
                  <span className="text-gray-500">+ Extra Human Agents ({addOns.extraHumanAgents})</span>
                  <span className="font-semibold text-gray-900">₹{(addOns.extraHumanAgents * 900).toLocaleString('en-IN')}</span>
                </div>
              )}
              {addOns?.extraCredits && (
                <div className="flex justify-between text-sm pl-3 border-l-2 border-[#82bbe0]/40">
                  <span className="text-gray-500">+ Credit Pack ({addOns.extraCredits.label || addOns.extraCredits.k})</span>
                  <span className="font-semibold text-gray-900">₹{addOns.extraCredits.price.toLocaleString('en-IN')}</span>
                </div>
              )}
            </>
          )}

          <div className="flex justify-between text-sm">
            <span className="text-gray-500">GST (18%)</span>
            <span className="font-semibold text-gray-900">₹{gst.toLocaleString('en-IN')}</span>
          </div>

          <div className="border-t border-gray-200/50 my-2 pt-2 flex justify-between text-sm font-bold text-gray-900">
            <span>Total Paid</span>
            <span>₹{amount?.toLocaleString('en-IN')}</span>
          </div>
        </div>
 
        <div className="flex gap-3">
          <button
            onClick={() => downloadReceipt({ paymentId, plan, amount, userName, addOns })}
            className="flex-1 flex items-center justify-center gap-2 py-3 border-2 border-gray-100 rounded-xl text-sm font-semibold text-gray-600 hover:border-[#82bbe0] hover:text-[#82bbe0] transition-all"
          >
            <Download size={15} /> Invoice
          </button>
          <button
            onClick={onDone}
            className="flex-1 flex items-center justify-center gap-2 py-3 bg-[#82bbe0] rounded-xl text-sm font-semibold text-white hover:bg-[#6aabce] transition-all shadow-lg shadow-[#82bbe0]/30"
          >
            Continue <ArrowRight size={15} />
          </button>
        </div>
      </div>
    </div>
  );
}